import { useState, useContext, useEffect } from "react";
import logo from "../../assets/images/logo.png";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Button,
  TextField,
  Typography,
  Link,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { clinicianLogin } from "../../services/apiCalls.js";
import { DetailsToMenu } from "../../components/Menu/DetailsToMenu.jsx";

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "ro", label: "RO" },
  { code: "lt", label: "LT" },
];

const Login = () => {
  const [username, setUsername] = useState(null);
  const [password, setPassword] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { checkAuthentication, handleSelectLanguage, language } =
    useContext(DetailsToMenu);
  const { t } = useTranslation("login");

  useEffect(() => {
    const sessionExpiredMessage = localStorage.getItem("sessionExpiredMessage");
    if (sessionExpiredMessage) {
      setError(sessionExpiredMessage);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(false);
    setUsernameError(false);

    if (!username) setUsernameError(true);
    if (!password) setPasswordError(true);

    if (username && password) {
      try {
        const credentials = { username, password };
        const result = await clinicianLogin(credentials);
        checkAuthentication();
        //console.log("Login successful:", result);
        navigate("/home");
      } catch (error) {
        setError(t("loginFailed"));
        //console.log("Login failed", error);
      }
    }
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handlePasswordBlur = () => setShowPassword(false);
  const handleMouseDownPassword = (e) => e.preventDefault();

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 15,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          src={logo}
          alt=""
          style={{ width: "100%", maxWidth: "500px", height: "auto" }}
        />
      </Box>
      <Box
        sx={{
          marginTop: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography component="h2" variant="h5" align="center">
          {t("welcome")}
        </Typography>
        <Typography variant="subtitle2" align="center">
          {t("subtitle")}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
          <TextField
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
            required
            fullWidth
            id="username"
            label={t("username")}
            name="username"
            autoComplete="username"
            autoFocus
            error={usernameError}
          />
          <TextField
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            fullWidth
            name="password"
            label={t("password")}
            type={showPassword ? "text" : "password"}
            id="password"
            autoComplete="current-password"
            error={passwordError}
            onBlur={handlePasswordBlur}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ textAlign: "right" }}>
            <Link
              href="#"
              variant="subtitle2"
              underline="none"
              color="secondary"
            >
              {t("forgotPassword")}
            </Link>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Button
              type="submit"
              size="medium"
              variant="contained"
              sx={{ mt: 1, mb: 2 }}
            >
              {t("signIn")}
            </Button>
            {error && <p style={{ color: "red" }}>{error}</p>}
            <Typography sx={{ fontSize: 12 }}>
              {t("noAccount")}{" "}
              <Link
                href="mailto:office@oncodir.eu"
                underline="none"
                color="secondary"
              >
                office@oncodir.eu
              </Link>
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 0.5 }}>
          {LANGUAGES.map((lng, index) => (
            <Box key={lng.code} sx={{ display: "flex", alignItems: "center" }}>
              {index > 0 && (
                <Typography sx={{ mx: 0.5, color: "text.secondary" }}>
                  |
                </Typography>
              )}
              <Button
                size="small"
                onClick={() => handleSelectLanguage(lng.code)}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  fontWeight: language === lng.code ? "bold" : "normal",
                  textDecoration:
                    language === lng.code ? "underline" : "none",
                  color:
                    language === lng.code ? "primary.main" : "text.secondary",
                }}
              >
                {lng.label}
              </Button>
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default Login;
